import { Button, Dialog, Text, XStack, YStack } from '@my/ui'
import { Clock } from '@tamagui/lucide-icons'
import { formatDuration, formatTimeRange } from 'app/utils/dates/timeFormatters'
import { useCallback, useEffect, useMemo } from 'react'

import { useTaskTimePicker } from '../../hooks/useTaskTimePicker'
import { DurationSelector } from './DurationSelector'
import { TimeSelector } from './TimeSelector'
interface TaskTimePickerProps {
  duration: number | null
  focusTime: string | null
  selectedDate: string | null
  onSave: (duration: number | null, focusTime: string | null) => void
}

/**
 * TaskTimePicker component allows users to select a duration and focus time for a task.
 */
export function TaskTimePicker({ duration, focusTime, selectedDate, onSave }: TaskTimePickerProps) {
  const {
    isOpen,
    setIsOpen,
    localDuration,
    localFocusTime,
    showCustomDuration,
    customDuration,
    handleDurationSelect,
    handleCustomDurationChange,
    handleTimeChange,
    handleSave,
  } = useTaskTimePicker(duration, focusTime, onSave)

  // Reset time state when dialog is opened or closed
  useEffect(() => {
    // This ensures we're properly syncing with the parent component's state
    if (!isOpen) {
      // When dialog closes, use the saved values or defaults from parent
      handleTimeChange(focusTime)
    }
  }, [isOpen, focusTime, handleTimeChange])

  //console.log('focusTime', focusTime)
  //console.log('localFocusTime', localFocusTime)

  const handleContinue = useCallback(() => {
    // We don't need to call handleTimeChange here as it may cause an additional update
    // and we already have the correct localFocusTime from the TimeSelector
    // Just call handleSave directly
    handleSave()
  }, [handleSave])

  const buttonText = useMemo(() => {
    // If no duration and no focus time, show default text
    if (!localDuration && !localFocusTime) return 'Set time'

    // If only duration is set
    if (localDuration && !localFocusTime) return `Anytime | ${formatDuration(localDuration)}`

    // If only focus time is set
    if (!localDuration && localFocusTime) return formatTimeRange(localFocusTime, 0)

    // If both are set
    if (localDuration && localFocusTime) {
      return `${formatTimeRange(localFocusTime, localDuration)} | ${formatDuration(localDuration)}`
    }

    return 'Set time'
  }, [localDuration, localFocusTime])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
    },
    [setIsOpen]
  )

  return (
    <>
      <Button unstyled alignSelf="flex-start" onPress={() => setIsOpen(true)}>
        <XStack
          borderWidth={2.5}
          borderColor={duration || focusTime ? '$accentColor' : '$color5'}
          paddingVertical="$1.5"
          paddingHorizontal="$2.5"
          borderRadius="$2"
          gap="$2"
          alignItems="center"
          alignSelf="flex-start"
          flexShrink={1}
          flexGrow={0}
          maxWidth="100%"
          cursor="pointer"
          hoverStyle={{ backgroundColor: '$color1' }}
        >
          <Clock
            strokeWidth={2.5}
            size={16}
            color={duration || focusTime ? '$accentColor' : '$color6'}
          />
          <Text
            fontSize="$3"
            fontWeight="500"
            color={duration || focusTime ? '$accentColor' : '$color6'}
          >
            {buttonText}
          </Text>
        </XStack>
      </Button>

      {isOpen && (
        <Dialog modal open={isOpen} onOpenChange={handleOpenChange}>
          <Dialog.Portal>
            <Dialog.Overlay key="setTimeOverlay" />
            <Dialog.Content
              key="setTimeContent"
              shadowColor="$color10"
              shadowRadius={10}
              shadowOffset={{ width: 0, height: 0 }}
              shadowOpacity={0.2}
              //width="95%"
              //maxWidth={600}
            >
              <Dialog.Title />
              <YStack gap="$2" padding="$2" marginTop="$2">
                <DurationSelector
                  localDuration={localDuration}
                  //showCustomDuration={showCustomDuration}
                  //customDuration={customDuration}
                  onDurationSelect={handleDurationSelect}
                  //onCustomDurationChange={handleCustomDurationChange}
                />
                <TimeSelector
                  selectedDate={selectedDate}
                  time={localFocusTime}
                  onChange={(newTime) => {
                    handleTimeChange(newTime)
                  }}
                />
                <XStack justifyContent="flex-end">
                  <Button theme="active" onPress={handleContinue} borderRadius="$8" height="$3.5">
                    Continue
                  </Button>
                </XStack>
              </YStack>
            </Dialog.Content>
          </Dialog.Portal>


          <Dialog.Adapt platform="native">
            <Dialog.Sheet animation="200ms" modal dismissOnSnapToBottom snapPointsMode="fit">
              <Dialog.Sheet.Frame padding="$4" gap="$4">
                <Dialog.Adapt.Contents />
              </Dialog.Sheet.Frame>
              <Dialog.Sheet.Overlay
                animation="quick"
                enterStyle={{ opacity: 0 }}
                exitStyle={{ opacity: 0 }}
              />
            </Dialog.Sheet>
          </Dialog.Adapt>
        </Dialog>
      )}
    </>
  )
}
